(ns practitest.frontend.project-fields-settings.index
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [practitest.frontend.common.mixpanel :as mixpanel]
   [practitest.frontend.components2.grid :as grid2]
   [practitest.frontend.components2.grid.action-cells :as action-cells]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.components2.dropdown :as dropdown]
   [practitest.frontend.components.card-view :as card-view]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.grid.localstorage :as grid-storage]
   [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.subs :as subs]
   [practitest.frontend.components.navigation :as navigation]
   [practitest.frontend.components.project-settings-tabs :as project-settings-tabs]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.forms3.custom-field-form-common :as cf-form-common]
   [practitest.frontend.project-fields-settings.events :as pf-events]
   [practitest.frontend.project-fields-settings.subs :as pf-subs]))

(def ^:private table-id "project-fields-index")
(def ^:private default-per-page 100)

(def ^:private entity-possible-values
  ["Issue" "Test" "TestSet" "Instance" "Run" "Requirement" "Milestone"])

(def ^:private field-format-possible-values
  ["Text" "Memo" "Number" "List" "Date" "Date Time"
   "Checkbox" "User" "Linked list" "Multi List" "URL"])

(defn- columns []
  [{:field         :name_link
    :header        (get-translation "generic.label.name" "Display Name")
    :type          :internal-url
    :default-width 200
    :sortable?     true
    :autofilter?   true}
   {:field           :type_label
    :header          (get-translation "custom-fields.type" "Type")
    :type            :list
    :multiple?       true
    :possible-values [(get-translation "custom-fields.type.system" "System Field")
                      (get-translation "custom-fields.type.custom" "Custom Field")
                      (get-translation "custom-fields.type.global" "Global Field")]
    :default-width   130
    :sortable?       true
    :autofilter?     true}
   {:field           :field_format
    :header          (get-translation "custom-fields.format" "Field Format")
    :type            :list
    :multiple?       true
    :possible-values field-format-possible-values
    :default-width   130
    :autofilter?     true
    :sortable?       true}
   {:field           :shown_in
    :header          (get-translation "custom-fields.shown-in" "Shown In")
    :type            :multilist
    :multiple?       true
    :possible-values entity-possible-values
    :default-width   200
    :autofilter?     true
    :sortable?       false}
   {:field           :available_in
    :header          (get-translation "custom-fields.available-in" "Available In")
    :type            :multilist
    :multiple?       true
    :possible-values entity-possible-values
    :default-width   200
    :autofilter?     true
    :sortable?       false}
   {:field         :updated_at
    :header        (get-translation "custom-fields.modified" "Modified")
    :type          :date-time
    :default-width 160
    :sortable?     true
    :autofilter?   true}])

(defn- action-cell [row]
  [action-cells/action-cells
   [{:label    (get-translation "generic.button.edit" "Edit")
     :icon     icons/mux-pencil
     :on-click #(set! (.-href js/window.location) (:edit_url row))}
    {:label        (get-translation "generic.button.delete" "Delete")
     :icon         icons/mux-trash
     :disabled?    (not (:deletable row))
     :tooltip      (when (not (:deletable row))
                     (if (:system row)
                       (get-translation "custom-fields.delete.disabled-tooltip.system" "System fields cannot be deleted")
                       (get-translation "custom-fields.delete.disabled-tooltip.global" "Global fields can only be deleted from account settings")))
     :on-click     (fn []
                     (mixpanel/track "Delete_Project_Field" {"Type" (:field_format row)})
                     (re-frame/dispatch [::pf-events/delete-field (:id row)]))
     :confirmation {:title (get-translation "custom-fields.confirm.delete.title" "Delete Field?")
                    :body  (get-translation "custom-fields.confirm.delete.body" "This field will be deleted permanently.")}}]])

(defn- private-usage-confirm-modal [message field-id]
  (let [state (reagent/atom
               {:show?   true
                :details {:title     (get-translation "custom-fields.confirm.private-usage.title" "Delete Field?")
                          :body      message
                          :yes-label (get-translation "generic.button.delete" "Delete")
                          :no-label  (get-translation "generic.button.cancel" "Cancel")
                          :on-yes    (fn []
                                       (re-frame/dispatch [::pf-events/force-delete-field field-id]))
                          :on-no     #(re-frame/dispatch [::pf-events/hide-delete-modal])}})]
    (fn [_message _field-id]
      [card-view/confirmation-dialog
       (reagent/cursor state [:show?])
       (reagent/cursor state [:details])])))

(defn- delete-modal-component []
  (let [modal (re-frame/subscribe [::pf-subs/delete-modal])]
    (fn []
      (let [{:keys [type message usages field-id]} @modal]
        (case type
          :blocked
          [cf-form-common/convert-blocked-alert
           {:show?     true
            :title     (get-translation "custom-fields.blocked.delete.title" "Cannot Delete Field")
            :message   message
            :usages    usages
            :on-cancel #(re-frame/dispatch [::pf-events/hide-delete-modal])}]

          :confirm
          [private-usage-confirm-modal message field-id]

          nil)))))

(defn- post-columns []
  [{:header        (get-translation "generic.label.actions" "Actions")
    :field         :actions
    :default-width 80
    :width         80
    :type          :custom
    :text-align    :center
    :resizable?    false
    :sortable?     false
    :systemcol?    true
    :class         ["action-menu"]
    :cell-fn       (fn [_ row _] [action-cell row])}])

(defn- toolbar [new-url section-entities]
  [:div.grid-toolbar-right.project-fields-toolbar
   (when (seq section-entities)
     (into [dropdown/dropdown-button
            {:text (get-translation "custom-fields.edit-form-layout" "Edit Form Layout")}
            {}]
           (for [{:keys [label url]} section-entities]
             ^{:key label}
             [dropdown/dropdown-menu-item {:text label :href url}])))
   [buttons/button
    {:primary? true
     :text     (get-translation "custom-fields.new" "New Custom Field")
     :href     new-url}]])

(defn main-panel []
  (let [grid-state        (reagent/atom {:module table-id})
        fields            (re-frame/subscribe [::pf-subs/fields])
        total-count       (re-frame/subscribe [::pf-subs/total-count])
        loading?          (re-frame/subscribe [::pf-subs/loading?])
        section-entities  (re-frame/subscribe [::pf-subs/section-entities])
        menu-collapsed?   (re-frame/subscribe [::navigation/project-level-menu-collapsed?])
        grid-toolbar-fn   (fn [_]
                            (let [new-url (broutes/get-url "custom_fields#new"
                                                           {:project_id (subs/get-current-project-id @re-frame.db/app-db)})]
                              [toolbar new-url @section-entities]))]
    (fn []
      [:div.project-fields-index-page
       [delete-modal-component]
       [:div.project-fields-header
        [project-settings-tabs/project-settings-tabs {}]
        [:h1.project-fields-page-title (get-translation "settings_sub_navbar.fields" "Fields")]]
       [:div.project-fields-content
        [grid2/grid
        {:columns              (columns)
         :post-columns         (post-columns)
         :full-data?           true
         :data                 (or @fields [])
         :total-count          @total-count
         :loading?             @loading?
         :resizable?           true
         :expanded?            @menu-collapsed?
         :on-expand-click      (fn [] (re-frame/dispatch [::navigation/set-project-level-menu-collapsed? (not @menu-collapsed?)]))
         :table-id             table-id
         :grid-state           grid-state
         :local-state-updating grid-state
         :grid-toolbar         grid-toolbar-fn
         :columns-widths-save  (reagent/partial grid-storage/save-column-sizes table-id)
         :columns-widths-load  (reagent/partial grid-storage/load-column-sizes table-id)
         :sort-order-save      (reagent/partial grid-session-storage/save-sort-order table-id)
         :sort-order-load      (reagent/partial grid-session-storage/load-sort-order table-id)
         :per-page-save        (reagent/partial grid-storage/save-per-page table-id)
         :per-page-load        #(or (grid-storage/load-per-page table-id) default-per-page)
         :current-page-save    (reagent/partial grid-storage/save-current-page table-id)
         :current-page-load    (reagent/partial grid-storage/load-current-page table-id)}]]])))
